/**
 * @module
 *
 * @import {ElementLinkCallback, InternalEngine} from '@acng/frontend-stargazer';
 * @import {FreeMovieData} from "../context/list";
 */
import {task} from '@acng/frontend-stargazer';
import {HIDDEN} from '@acng/frontend-bounty/dom/attribute.js';
import {now} from '@acng/frontend-bounty/std/date.js';
import {remove} from '@acng/frontend-bounty/dom/move.js';

import {user} from '../../user/user.js';
import {createFrontendFeature} from '../../core/model/feature.js';
import {hasFeature} from '../../core/service/env.js';
import {get as getMovie} from '../../moviePool/service/cache.js';
import {get as getCreator} from '../../amateurPool/service/cache.js';
import {t} from '../../locale/config/translate.js';
import {init} from '../context/list.js';
import {MovieTemplateConfig, MovieLink} from 'acng/moviePool/service/template.js';

/**
 * Stargazer **FMOTD Feature**
 */
export const feature = createFrontendFeature('fmotd');

feature[HIDDEN] = !hasFeature('moviePool');

/**
 * Setup the free movie cache as compile task or, if the feature is hidden,
 * remove the element.
 *
 * @param {Element} element
 * @param {string} _value
 * @param {InternalEngine} compiler The setup task would be added to
 *
 * @returns {void}
 */
export const compile = (element, _value, compiler) => {
  if (feature[HIDDEN]) {
    remove(element);
  } else {
    task(compiler, init());
  }
};

/**
 * Use default movie template config with modified title, image and href.
 *
 * TODO Voyager should be capable of handling the special route behaviour.
 *
 * @param {FreeMovieData} freemovie
 *
 * @returns {Promise<ElementLinkCallback<HTMLElement>>}
 */
export const ListItem = async (freemovie) => {
  const movie = await getMovie(freemovie.id);
  const creator = await getCreator(`${freemovie.amateur_id}`);
  const config = await MovieTemplateConfig(movie, creator);

  config.name = await makeTitle(freemovie, config.name);
  config.url += '?fmotd';

  if (freemovie.use_profile_image) {
    config.image = creator.getImageUrl('800');
  }

  ASSERT: if (`${freemovie.amateur_id}` != creator.id) {
    console.warn('freemovie amateur does not match (displayed) movie amateur');
  }

  return MovieLink(movie, config);
};

/**
 * @param {import('../context/list.js').FreeMovieData} free
 * @param {string | undefined} movieTitle
 */
const makeTitle = async ({free_since}, movieTitle) => {
  let prefix = '';
  const currentTime = now();
  const countdown = free_since - currentTime;
  const sinceDate = new Date(free_since);
  const oneDayDuration = 24 * 36e5;

  if (countdown <= 0) {
    if (countdown <= -oneDayDuration) {
      prefix = sinceDate.toLocaleDateString(user.lang, {
        month: 'long',
        day: 'numeric',
      });
    }
  } else if (countdown < oneDayDuration) {
    if (sinceDate.getDay() == new Date().getDay()) {
      prefix = await t('moviePool.freemovie.time');
    } else {
      prefix = await t('moviePool.freemovie.next2');
    }
  } else {
    prefix = sinceDate.toLocaleDateString(user.lang, {weekday: 'long'});
  }

  return `${prefix}${prefix ? ': ' : ''}${movieTitle}`;
};
